import { isSilentFailure, isUnansweredRequest } from '@/lib/template-admin';
import { TEMPLATE_COVER } from '@/lib/template-labels';

// The browser's checks on a template cover before it is sent: its type, its size in bytes, its WebP header (not
// animated) and the picture's size. The server checks the same again; these spare staff a failed upload.
// Design: docs/specs/2026-10-13-template-covers-and-gallery-design.md (Backoffice)

export const COVER_NOT_WEBP = 'Use a WebP image';
export const COVER_TOO_BIG = 'Use an image up to 1 MB';
export const COVER_ANIMATED = 'Use a still image, not an animation';

/** What is wrong with a chosen file before it is decoded (its type, then its size); null when nothing is. */
export function coverFileProblem(file: { name: string; type: string; size: number }): string | null {
  // Some systems give a .webp file no type, so the extension counts too.
  if (file.type !== TEMPLATE_COVER.type && !/\.webp$/i.test(file.name)) return COVER_NOT_WEBP;
  if (file.size > TEMPLATE_COVER.maxBytes) return COVER_TOO_BIG;
  return null;
}

/** The bytes coverHeaderProblem reads: the RIFF header and the first chunk's header and flags. */
export const COVER_HEADER_BYTES = 30;

const ascii = (bytes: Uint8Array, at: number) => String.fromCharCode(...bytes.subarray(at, at + 4));

/**
 * What is wrong with a file's first bytes, by the server's WebP rules: a RIFF/WEBP file whose first chunk is
 * VP8, VP8L or VP8X, and a VP8X without the animation flag. The browser decodes only an animation's first frame,
 * so this is the only place it can see one. null when nothing is.
 */
export function coverHeaderProblem(bytes: Uint8Array): string | null {
  if (bytes.length < COVER_HEADER_BYTES || ascii(bytes, 0) !== 'RIFF' || ascii(bytes, 8) !== 'WEBP') {
    return COVER_NOT_WEBP;
  }
  const chunk = ascii(bytes, 12);
  if (chunk !== 'VP8 ' && chunk !== 'VP8L' && chunk !== 'VP8X') return COVER_NOT_WEBP;
  if (chunk === 'VP8X' && (bytes[20] & 0x02) !== 0) return COVER_ANIMATED;
  return null;
}

/** The line for a picture that isn't exactly 1200 x 900; null when it is. */
export function coverDimensionsProblem(width: number, height: number): string | null {
  if (width === TEMPLATE_COVER.width && height === TEMPLATE_COVER.height) return null;
  return `Use an image ${TEMPLATE_COVER.width} x ${TEMPLATE_COVER.height} px. This one is ${width} x ${height}`;
}

type Measure = (file: Blob) => Promise<{ width: number; height: number }>;

/** The picture's size, decoded by the browser; rejects when it can't be decoded. */
const measureImage: Measure = async (file) => {
  const bitmap = await createImageBitmap(file);
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return size;
};

/** Checks a chosen file as far as the browser can: the line to show, or null when it can be uploaded. */
export async function checkCoverFile(file: File, measure: Measure = measureImage): Promise<string | null> {
  const problem = coverFileProblem(file);
  if (problem) return problem;
  try {
    const header = coverHeaderProblem(new Uint8Array(await file.slice(0, COVER_HEADER_BYTES).arrayBuffer()));
    if (header) return header;
    const { width, height } = await measure(file);
    return coverDimensionsProblem(width, height);
  } catch {
    // The file couldn't be read or decoded (another format with a .webp name, or a broken file).
    return COVER_NOT_WEBP;
  }
}

/** Shown when a cover write got no answer; the page then reloads to show whether it went through. */
export const COVER_UNANSWERED_TEXT = 'No answer from the server, reloading to check the cover';

/**
 * True when a cover upload or remove got no usable answer (no response, or a gateway page without a message):
 * it may or may not have happened, so the page reloads. A server answer with a message (an invalid file, a
 * conflict, storage that failed) was already toasted by the api client.
 */
export function isUnansweredCoverWrite(error: unknown): boolean {
  return isUnansweredRequest(error) && isSilentFailure(error);
}
